/**
 * Super Admin - Reservations Management
 * View and manage all bookings with financial details
 */

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";

// react-big-calendar
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { superAdminAPI } from "utils/api";

// Icons
import Icon from "@mui/material/Icon";

const localizer = momentLocalizer(moment);

function ReservationsManagement() {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [financialSummary, setFinancialSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [financialLoading, setFinancialLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Calendar state
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    status: "all",
    propertyId: "all",
  });

  // Dialog
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // Tabs
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchReservations();
    fetchFinancialSummary();
  }, [filters]);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setMessage({ type: "", text: "" });

      const queryParams = {
        limit: 10000, // Get all reservations for calendar
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (filters.status !== "all") {
        queryParams.status = filters.status;
      }
      if (filters.propertyId !== "all") {
        queryParams.propertyId = filters.propertyId;
      }

      const response = await superAdminAPI.getAllReservations(queryParams);
      setReservations(response.data || []);
    } catch (error) {
      console.error("Error fetching reservations:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load reservations",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      setFinancialLoading(true);
      // Calculate financial summary from reservations
      const allReservations = await superAdminAPI.getAllReservations({ limit: 10000 });
      const reservationsList = allReservations.data || [];

      let totalRevenue = 0;
      let totalPlatformFees = 0;
      let totalDeductions = 0;
      let totalInsuranceDeposits = 0;
      let checkedOutRevenue = 0;
      let pendingRevenue = 0;

      reservationsList.forEach((reservation) => {
        const totalAmount = reservation.pricing?.totalAmount || 0;
        const platformFee = reservation.pricing?.platformFee || 0;
        const deductions = reservation.checkOut?.additionalCharges || 0;
        const insuranceDeposit = reservation.pricing?.insurancePrice || 0;

        totalRevenue += totalAmount;
        totalPlatformFees += platformFee;
        totalDeductions += deductions;
        totalInsuranceDeposits += insuranceDeposit;

        if (reservation.status === "CheckedOut") {
          checkedOutRevenue += totalAmount;
        } else if (reservation.status === "Confirmed" || reservation.status === "CheckedIn") {
          pendingRevenue += totalAmount;
        }
      });

      setFinancialSummary({
        totalRevenue,
        totalPlatformFees,
        totalDeductions,
        totalInsuranceDeposits,
        checkedOutRevenue,
        pendingRevenue,
        netRevenue: totalRevenue - totalPlatformFees - totalDeductions,
      });
    } catch (error) {
      console.error("Error fetching financial summary:", error);
    } finally {
      setFinancialLoading(false);
    }
  };

  // Convert reservations to calendar events
  const calendarEvents = useMemo(() => {
    return reservations.map((reservation) => {
      // Determine color based on status
      let color = "#1976d2"; // Default blue for Confirmed
      if (reservation.status === "CheckedIn") color = "#4caf50"; // Green
      else if (reservation.status === "CheckedOut") color = "#9e9e9e"; // Gray
      else if (reservation.status === "Cancelled" || reservation.status === "Refunded")
        color = "#f44336"; // Red

      return {
        id: reservation._id || reservation.id,
        title: `${reservation.property?.title?.en || reservation.property?.title || "Property"} - ${
          reservation.reservationNumber || "N/A"
        }`,
        start: new Date(reservation.checkInDate),
        end: new Date(reservation.checkOutDate),
        resource: reservation,
        color: color,
      };
    });
  }, [reservations]);

  // Event style getter
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color || "#1976d2",
        borderRadius: "4px",
        opacity: 0.9,
        color: "white",
        border: "none",
        display: "block",
        padding: "4px 8px",
        fontSize: "0.875rem",
        fontWeight: 500,
      },
    };
  };

  // Handle event click
  const handleSelectEvent = async (event) => {
    try {
      const response = await superAdminAPI.getReservationById(
        event.resource._id || event.resource.id
      );
      setSelectedReservation(response.data);
      setSelectedEvent(event.resource);
      setDetailsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching reservation details:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to load reservation details",
      });
    }
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
  };

  // Handle navigation
  const handleNavigate = (newDate) => {
    setDate(newDate);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EGP",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("MMM DD, YYYY");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Confirmed":
        return "info";
      case "CheckedIn":
        return "success";
      case "CheckedOut":
        return "secondary";
      case "Cancelled":
      case "Refunded":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold" mb={1}>
            Reservations Management
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            View and manage all bookings across all properties with financial details
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
          <Tab label="Reservations" />
          <Tab label="Financial Summary" />
        </Tabs>

        {tabValue === 0 && (
          <>
            {/* Filters */}
            <Card sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="Confirmed">Confirmed</MenuItem>
                      <MenuItem value="CheckedIn">Checked In</MenuItem>
                      <MenuItem value="CheckedOut">Checked Out</MenuItem>
                      <MenuItem value="Cancelled">Cancelled</MenuItem>
                      <MenuItem value="Refunded">Refunded</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setFilters({
                        status: "all",
                        propertyId: "all",
                      });
                    }}
                    fullWidth
                  >
                    Reset Filters
                  </MDButton>
                </Grid>
              </Grid>
            </Card>

            {/* Calendar Controls */}
            <Card sx={{ p: 3, mb: 3 }}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" gap={1}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() =>
                      setDate(
                        moment(date)
                          .subtract(
                            1,
                            view === "month" ? "month" : view === "week" ? "week" : "day"
                          )
                          .toDate()
                      )
                    }
                    startIcon={<Icon>chevron_left</Icon>}
                  >
                    Previous
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => setDate(new Date())}
                  >
                    Today
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() =>
                      setDate(
                        moment(date)
                          .add(1, view === "month" ? "month" : view === "week" ? "week" : "day")
                          .toDate()
                      )
                    }
                    endIcon={<Icon>chevron_right</Icon>}
                  >
                    Next
                  </MDButton>
                </MDBox>
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    variant={view === "month" ? "contained" : "outlined"}
                    onClick={() => handleViewChange("month")}
                  >
                    Month
                  </Button>
                  <Button
                    variant={view === "week" ? "contained" : "outlined"}
                    onClick={() => handleViewChange("week")}
                  >
                    Week
                  </Button>
                  <Button
                    variant={view === "day" ? "contained" : "outlined"}
                    onClick={() => handleViewChange("day")}
                  >
                    Day
                  </Button>
                </ButtonGroup>
              </MDBox>
            </Card>

            {/* Calendar */}
            <Card sx={{ p: 3 }}>
              {loading ? (
                <MDBox display="flex" justifyContent="center" alignItems="center" py={8}>
                  <CircularProgress />
                </MDBox>
              ) : reservations.length === 0 ? (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={8}
                  sx={{ minHeight: "500px" }}
                >
                  <Icon sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}>event_busy</Icon>
                  <MDTypography variant="h6" color="text.secondary" mb={1}>
                    No Reservations Found
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    No reservations match your filters. Try adjusting your filters or check back
                    later.
                  </MDTypography>
                </MDBox>
              ) : (
                <>
                  <MDBox
                    sx={{
                      height: view === "month" ? "600px" : view === "week" ? "500px" : "400px",
                      "& .rbc-calendar": {
                        fontFamily: "inherit",
                      },
                      "& .rbc-header": {
                        padding: "12px 8px",
                        fontWeight: 600,
                        borderBottom: "2px solid #e0e0e0",
                      },
                      "& .rbc-today": {
                        backgroundColor: "rgba(25, 118, 210, 0.1)",
                      },
                      "& .rbc-off-range-bg": {
                        backgroundColor: "#f5f5f5",
                      },
                      "& .rbc-event": {
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      },
                      "& .rbc-event:hover": {
                        opacity: 0.8,
                      },
                    }}
                  >
                    <Calendar
                      localizer={localizer}
                      events={calendarEvents}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: "100%" }}
                      view={view}
                      date={date}
                      onView={handleViewChange}
                      onNavigate={handleNavigate}
                      onSelectEvent={handleSelectEvent}
                      eventPropGetter={eventStyleGetter}
                      popup
                      showMultiDayTimes
                    />
                  </MDBox>

                  {/* Status Legend */}
                  <MDBox mt={3}>
                    <Card sx={{ p: 2 }}>
                      <MDBox display="flex" gap={3} alignItems="center" flexWrap="wrap">
                        <MDTypography variant="button" fontWeight="medium">
                          Status Legend:
                        </MDTypography>
                        <Chip
                          label="Confirmed"
                          sx={{ bgcolor: "#1976d2", color: "white", fontWeight: 600 }}
                          size="small"
                        />
                        <Chip
                          label="Checked In"
                          sx={{ bgcolor: "#4caf50", color: "white", fontWeight: 600 }}
                          size="small"
                        />
                        <Chip
                          label="Checked Out"
                          sx={{ bgcolor: "#9e9e9e", color: "white", fontWeight: 600 }}
                          size="small"
                        />
                        <Chip
                          label="Cancelled"
                          sx={{ bgcolor: "#f44336", color: "white", fontWeight: 600 }}
                          size="small"
                        />
                      </MDBox>
                    </Card>
                  </MDBox>
                </>
              )}
            </Card>
          </>
        )}

        {tabValue === 1 && (
          <Grid container spacing={3}>
            {financialLoading ? (
              <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                </Card>
              </Grid>
            ) : financialSummary ? (
              <>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Total Revenue
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.totalRevenue)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Platform Fees
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.totalPlatformFees)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Net Revenue
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.netRevenue)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Checked Out Revenue
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.checkedOutRevenue)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Pending Revenue
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.pendingRevenue)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Total Deductions
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.totalDeductions)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Card
                    sx={{ p: 3, background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)" }}
                  >
                    <MDBox>
                      <MDTypography variant="caption" color="white" fontWeight="medium">
                        Insurance Deposits
                      </MDTypography>
                      <MDTypography variant="h4" color="white" fontWeight="bold">
                        {formatCurrency(financialSummary.totalInsuranceDeposits)}
                      </MDTypography>
                    </MDBox>
                  </Card>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                  <MDBox py={4} textAlign="center">
                    <MDTypography variant="body2" color="text.secondary">
                      No financial data available
                    </MDTypography>
                  </MDBox>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {/* Reservation Details Dialog */}
        <Dialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h5" fontWeight="bold">
                Reservation Details
              </MDTypography>
              <IconButton onClick={() => setDetailsDialogOpen(false)}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>
          </DialogTitle>
          <DialogContent dividers>
            {selectedReservation && (
              <MDBox>
                {/* Check-in/Check-out Agent */}
                {(() => {
                  const agent =
                    selectedReservation.assignedCheckInOutAgent ||
                    selectedReservation.property?.assignedCheckInOutAgent;
                  return agent ? (
                    <MDBox
                      sx={{
                        bgcolor: "info.lighter",
                        p: 2,
                        borderRadius: 2,
                        mb: 3,
                        border: "1px solid",
                        borderColor: "info.light",
                      }}
                    >
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        mb={1}
                        color="info.dark"
                        display="flex"
                        alignItems="center"
                        gap={1}
                      >
                        <Icon sx={{ fontSize: 18 }}>person</Icon>
                        Check-in/Check-out Agent (this property)
                      </MDTypography>
                      <MDBox display="flex" flexDirection="column" gap={0.5}>
                        <MDTypography variant="body2">
                          <strong>Name:</strong> {agent.fullName || agent.firstName || "—"}
                        </MDTypography>
                        <MDTypography variant="body2">
                          <strong>Email:</strong> {agent.email || "—"}
                        </MDTypography>
                        <MDTypography variant="body2">
                          <strong>Phone:</strong> {agent.phone || agent.companyNumber || "—"}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  ) : null;
                })()}

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Property Name
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold">
                    {selectedReservation.property?.title?.en ||
                      selectedReservation.property?.title ||
                      "N/A"}
                  </MDTypography>
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Reservation Number
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">
                    {selectedReservation.reservationNumber || "N/A"}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Booking Dates
                  </MDTypography>
                  <MDTypography variant="body1">
                    {formatDate(selectedReservation.checkInDate)} -{" "}
                    {formatDate(selectedReservation.checkOutDate)}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Status
                  </MDTypography>
                  <Chip
                    label={selectedReservation.status || "N/A"}
                    color={getStatusColor(selectedReservation.status)}
                    size="small"
                  />
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Number of Guests
                  </MDTypography>
                  <MDTypography variant="body1">
                    {selectedReservation.numberOfGuests || 0}
                  </MDTypography>
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox mb={2}>
                  <MDTypography variant="h6" fontWeight="bold" mb={1}>
                    Guest Names (from Check-In)
                  </MDTypography>
                  {(() => {
                    const guests = selectedReservation.checkIn?.guests;
                    const hasGuests = Array.isArray(guests) && guests.length > 0;
                    const clientName =
                      selectedReservation.client?.fullName ||
                      [selectedReservation.client?.firstName, selectedReservation.client?.lastName]
                        .filter(Boolean)
                        .join(" ") ||
                      selectedReservation.client?.email;
                    const fallbackGuests =
                      !hasGuests &&
                      (selectedReservation.status === "CheckedIn" ||
                        selectedReservation.status === "CheckedOut") &&
                      clientName
                        ? [{ name: clientName }]
                        : [];
                    const displayGuests = hasGuests ? guests : fallbackGuests;
                    return displayGuests.length > 0 ? (
                      <MDBox>
                        {displayGuests.map((guest, index) => (
                          <MDTypography key={index} variant="body2" fontWeight="medium" mb={0.5}>
                            • Guest {index + 1}: {guest.name || "N/A"}
                          </MDTypography>
                        ))}
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text.secondary">
                        No guest details yet. Guest names will appear after check-in.
                      </MDTypography>
                    );
                  })()}
                </MDBox>

                {/* Inspector Note (Check-in Agent Notes) */}
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Inspector Note
                  </MDTypography>
                  <MDTypography variant="body2">
                    {selectedReservation.checkIn?.notes || "No inspector note recorded."}
                  </MDTypography>
                </MDBox>

                {/* Special Requests (Guest Notes) */}
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Special Requests
                  </MDTypography>
                  <MDTypography variant="body2">
                    {selectedReservation.specialRequests || "No special requests from guest."}
                  </MDTypography>
                </MDBox>

                <Divider sx={{ my: 2 }} />

                <MDBox mb={2}>
                  <MDTypography variant="h6" fontWeight="bold" mb={1}>
                    Financial Details
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Total Amount (Client Paid)
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" color="success.main">
                    {formatCurrency(selectedReservation.pricing?.totalAmount || 0)}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Platform Revenue (Fee)
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="bold" color="warning.main">
                    {formatCurrency(
                      ((selectedReservation.pricing?.totalAmount || 0) *
                        (selectedReservation.property?.profitPercentage || 0)) /
                        100
                    )}
                  </MDTypography>
                </MDBox>

                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Insurance Deposit
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">
                    {formatCurrency(
                      selectedReservation.pricing?.insurancePrice ||
                        selectedReservation.property?.insurancePrice ||
                        0
                    )}
                  </MDTypography>
                </MDBox>

                {selectedReservation.checkOut?.additionalCharges > 0 && (
                  <MDBox mb={2}>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      fontWeight="medium"
                      display="block"
                      mb={0.5}
                    >
                      Additional Charges
                    </MDTypography>
                    <MDTypography variant="body1" fontWeight="medium" color="info.main">
                      {formatCurrency(selectedReservation.checkOut?.additionalCharges)}
                    </MDTypography>
                  </MDBox>
                )}
                <Divider sx={{ my: 2 }} />
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                    display="block"
                    mb={0.5}
                  >
                    Property Owner Revenue
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="bold" color="info.main">
                    {formatCurrency(
                      (selectedReservation.pricing?.totalAmount || 0) -
                        ((selectedReservation.pricing?.totalAmount || 0) *
                          (selectedReservation.property?.profitPercentage || 0)) /
                          100 -
                        (selectedReservation.checkOut?.additionalCharges || 0)
                    )}
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: "grey.50" }}>
            {selectedReservation?._id || selectedReservation?.id ? (
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  setDetailsDialogOpen(false);
                  navigate(
                    `/super-admin/reservations/${selectedReservation._id || selectedReservation.id}`
                  );
                }}
                sx={{ borderRadius: 2, mr: 1 }}
              >
                View Full Details
              </MDButton>
            ) : null}
            <MDButton onClick={() => setDetailsDialogOpen(false)} sx={{ borderRadius: 2 }}>
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ReservationsManagement;
